/* eslint no-unused-vars: "error"*/
import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Spinner } from 'react-bootstrap';

import { STATE_STATUS } from '@awp/constants';
import { fetchMultiStationData } from '@awp/stores/dataVisualizationSlice';

import GraphContainer from '@components/Graphs/MultiStation/Container';
import { useMultiStationContext } from '@components/Graphs/MultiStation/MultiStationContext';

// Styles for the overlay that displays while loading data
let overlayStyle = {
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: 'rgba(255, 255, 255, 0.5)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 1000,
};

// Styles for the spinner shown during data load
let spinnerStyle = {
  position: 'fixed',
  left: '50%',
  top: '50%',
  transform: 'translate(-50%, -50%)',
  zIndex: 1001,
};

/**
 * A component that manages the rendering of graph data based on the state managed in MultiStationContext.
 * It handles data fetching and displays graphs or loading indicators based on data availability and loading state.
 *
 * @returns {JSX.Element} - A component that conditionally renders graphs or a loading spinner.
 */
const GraphRenderer = () => {
  const { status, weatherStations, preset, presetId, weatherStationId, dataRange, isSelectedDeferOnlyPreset } =
    useMultiStationContext();
  const [loading, setLoading] = useState(false);
  const dispatch = useDispatch();

  useEffect(() => {
    if (loading) return;

    if (weatherStations?.length === 0 && weatherStationId == -1) return;
    if (!presetId) return;

    if (status.multiStationData === STATE_STATUS.LOADING || status.additionalMultiStationData === STATE_STATUS.LOADING)
      return;

    const fetchData = async () => {
      setLoading(true); // Set loading state to true when data fetching starts
      await dispatch(
        fetchMultiStationData({
          weatherStationIds: weatherStations.map((wx) => wx.Id),
          weatherStationId,
          presetId,
          forceFetch: false,
          endDate: dataRange.endDate,
          displayRange: dataRange.displayRange,
          isForGraph: true,
          isSelectedDeferOnlyPreset: isSelectedDeferOnlyPreset,
        }),
      );
      setLoading(false); // Set loading state to false when data fetching completes
    };

    fetchData();

    return () => {
      setLoading(false);
    };
  }, [weatherStations, weatherStationId, presetId, dataRange]);

  return (
    <>
      {preset ? (
        <>
          {preset?.DataGraphs?.map((dg) => {
            return <GraphContainer key={dg.DataGraphId} dataGraph={dg} />;
          })}
        </>
      ) : null}

      {loading && (
        <>
          <div style={overlayStyle}></div>
          <div>
            <div className="text-center mt-4" style={spinnerStyle}>
              <p>Loading Data...</p>
              <Spinner animation="border" />
            </div>
          </div>
        </>
      )}
    </>
  );
};

export default GraphRenderer;
